import { Refresh as RefreshIcon } from "@mui/icons-material";
import {
  Badge,
  Box,
  Chip,
  CircularProgress,
  IconButton,
  LinearProgress,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  Tooltip,
  Typography,
} from "@mui/material";
import AppStateContext from "contexts/AppStateContext";
import { useToast } from "contexts/ToastContext";
import useStateWithLocalStorage from "hooks/UseStateWithLocalStorage";
import { useContext, useEffect, useState } from "react";
import { useHistory } from "react-router";
import {
  DELIVERY_METHODS,
  DELIVERY_METHOD_LABELS,
  DeliveryMethod,
  PackingQueueRowType,
} from "types/ShippingTypes";
import { apiGetPackingQueue } from "util/network/Shipping";
import { ShipmentPage, errorText, formatDate } from "./common";

/** Orders waiting for the warehouse, one tab per delivery method. An order
 *  stays here until every line is in a shipment. */
const PackingQueue: React.FC = () => {
  const { token } = useContext(AppStateContext);
  const { addToast } = useToast();
  const history = useHistory();
  const [tab, setTab] = useStateWithLocalStorage<DeliveryMethod>(
    "delivery",
    "packingQueueTab"
  );
  const [rows, setRows] = useState<PackingQueueRowType[]>([]);
  const [loading, setLoading] = useState(true);

  const load = () => {
    setLoading(true);
    apiGetPackingQueue(token)
      .then(setRows)
      .catch((error) => addToast({ type: "error", message: errorText(error) }))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    if (token) load();
  }, [token]);

  const visible = rows.filter((row) => row.deliveryMethod === tab);

  return (
    <ShipmentPage
      title="Packing"
      actions={
        <Tooltip title="Refresh">
          <IconButton onClick={load} sx={{ color: "#fff" }}>
            <RefreshIcon />
          </IconButton>
        </Tooltip>
      }
    >
      <Tabs value={tab} onChange={(_, value) => setTab(value)} sx={{ mb: 2 }}>
        {DELIVERY_METHODS.map((method) => {
          const count = rows.filter((r) => r.deliveryMethod === method).length;
          return (
            <Tab
              key={method}
              value={method}
              label={
                <Badge
                  color="primary"
                  badgeContent={count}
                  sx={{ pr: count ? 1.5 : 0 }}
                >
                  {DELIVERY_METHOD_LABELS[method]}
                </Badge>
              }
            />
          );
        })}
      </Tabs>
      {loading ? (
        <CircularProgress />
      ) : visible.length === 0 ? (
        <Typography color="text.secondary">Nothing to pack.</Typography>
      ) : (
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Order</TableCell>
                <TableCell>Customer</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Left to pack</TableCell>
                <TableCell>Ready to pack</TableCell>
                <TableCell>Deadline</TableCell>
                <TableCell>Issues</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {visible.map((row) => {
                const allReady = row.readyLines >= row.lineCount;
                return (
                  <TableRow
                    key={row.orderId}
                    hover
                    sx={{ cursor: "pointer" }}
                    onClick={() =>
                      history.push(`/shipment/packing/${row.orderId}`)
                    }
                  >
                    <TableCell>
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 1 }}
                      >
                        <strong>{row.orderNo}</strong>
                        {row.isSplit && (
                          <Tooltip title="Part of this order has already gone out">
                            <Chip size="small" color="info" label="Split" />
                          </Tooltip>
                        )}
                        {!row.isSplit && row.hasActiveShipments && (
                          <Tooltip title="A label is booked but not scanned yet">
                            <Chip size="small" label="Booked" />
                          </Tooltip>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>{row.customer}</TableCell>
                    <TableCell>{row.status}</TableCell>
                    <TableCell>
                      {row.lines.slice(0, 3).map((line) => (
                        <Typography
                          key={line.orderItemId}
                          variant="caption"
                          display="block"
                        >
                          {line.quantity} × {line.name}
                        </Typography>
                      ))}
                      {row.lines.length > 3 && (
                        <Typography variant="caption" color="text.secondary">
                          + {row.lines.length - 3} more lines
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell sx={{ minWidth: 120 }}>
                      <Tooltip title="Lines through their last QC step. Lines without a production count as ready.">
                        <Box>
                          <Typography variant="caption">
                            {allReady
                              ? "All ready"
                              : row.readyLines === 0
                              ? "In production"
                              : `${row.readyLines} of ${row.lineCount} lines ready`}
                          </Typography>
                          <LinearProgress
                            variant="determinate"
                            color={allReady ? "success" : "warning"}
                            value={(100 * row.readyLines) / row.lineCount}
                          />
                        </Box>
                      </Tooltip>
                    </TableCell>
                    <TableCell>{formatDate(row.deadline)}</TableCell>
                    <TableCell>
                      {row.openIssues > 0 && (
                        <Chip
                          size="small"
                          color="warning"
                          label={`${row.openIssues} open`}
                        />
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </ShipmentPage>
  );
};

export default PackingQueue;
